import { type AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';

import type CarrierBuildingLocation from '~/types/CarrierBuildingLocation';

import { ActionKeys } from '~/actions/constants';
import { showDialog } from '~/actions/dialog.actions';
import * as pickupsActions from '~/actions/pickups.actions';
import * as pickupsService from '~/services/pickups.service';
import { type JSONCarrierAccount } from '~/types/CarrierAccount';
import { type JSONRate } from '~/types/Rate';
import { observability } from '~/utils/observability';

function* getTransactions(action: ReturnType<typeof pickupsActions.getTransactionsForCarrier>) {
  try {
    const result: AxiosResponse<{
      count: number;
      results: { object_id: string; rate: JSONRate }[];
      totals: { total_weight: string };
    }> = yield call(
      pickupsService.getTransactions,
      action.payload.carrier,
      action.payload.startDate,
      action.payload.endDate,
    );

    const totalTransactions = result.data.count;
    const transactionObjectIds: string[] = result.data.results.map(
      (transaction: { object_id: string }) => transaction.object_id,
    );

    const serviceLevelInfo = result.data.results.reduce<Record<string, number>>(
      (obj, transaction) => {
        const serviceLevelName = transaction.rate.servicelevel_name;
        if (obj[serviceLevelName as string])
          return { ...obj, [serviceLevelName as string]: obj[serviceLevelName as string] + 1 };

        return { ...obj, [serviceLevelName as string]: 1 };
      },
      {},
    );

    const payload = {
      serviceLevelInfo,
      totalTransactions,
      // TODO: This rounding is for presentational purposes and should happen in the view
      totalWeight: Math.round(100 * Number(result.data.totals.total_weight)) / 100,
      transactionObjectIds,
    };

    yield put(pickupsActions.getTransactionsForCarrierSuccess(payload));
  } catch (err) {
    const errorMessage =
      'There is an error from the carrier that requires investigation. Please contact support.';
    const payload = {
      closeButtonText: 'Close',
      messages: [errorMessage],
    };
    yield put(pickupsActions.getTransactionsForCarrierError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

function* getCarrierAccountId(action: ReturnType<typeof pickupsActions.getCarrierAccountId>) {
  try {
    const result: AxiosResponse<{ results: JSONCarrierAccount[] }> = yield call(
      pickupsService.getCarrierAccounts,
      action.payload.carrier,
    );

    let carrierAccountId: null | string | undefined = null;
    for (const carrier of result.data.results)
      if (carrier.is_shippo_account) carrierAccountId = carrier.object_id;

    const payload = {
      carrierAccountId,
    };
    yield put(pickupsActions.getCarrierAccountIdSuccess(payload));
  } catch (err) {
    const errorMessage =
      'There is an error from the carrier that requires investigation. Please contact support.';
    const payload = {
      closeButtonText: 'Close',
      messages: [errorMessage],
    };
    yield put(pickupsActions.getCarrierAccountIdError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

function* getCarrierBuildingLocationTypes(
  action: ReturnType<typeof pickupsActions.getCarrierBuildingLocationTypes>,
) {
  try {
    const result: AxiosResponse<CarrierBuildingLocation[]> = yield call(
      pickupsService.getCarrierBuildingLocationTypes,
      action.payload.carrier,
    );

    const payload = result.data;
    yield put(pickupsActions.getCarrierBuildingLocationTypesSuccess(payload));
  } catch (err) {
    const errorMessage =
      'There is an error from the carrier that requires investigation. Please contact support.';
    const payload = {
      closeButtonText: 'Close',
      messages: [errorMessage],
    };
    yield put(pickupsActions.getCarrierBuildingLocationTypesError(errorMessage));
    yield put(showDialog(payload, 'error'));
  }
}

function* sendPickupsRequest(action: ReturnType<typeof pickupsActions.sendPickupsRequest>) {
  try {
    // TODO: Type this and add toJSON function
    const country = action.payload.address.country ? action.payload.address.country.iso2 : 'US';
    const requestPayload = {
      carrier_account: action.payload.carrierAccountId,
      is_test: ENV_ID !== 'production',
      location: {
        address: {
          city: action.payload.address.city,
          company: action.payload.address.company,
          country,
          email: action.payload.address.email,
          name: action.payload.address.name,
          phone: action.payload.address.phone,
          state: action.payload.address.state,
          street1: action.payload.address.street1,
          street2: action.payload.address.street2,
          zip: action.payload.address.zip,
        },
        building_location_type: action.payload.buildingLocationType.label,
        building_type: '',
        instructions: action.payload.buildingLocationType.label,
      },
      metadata: '',
      requested_end_time: action.payload.endTime,
      requested_start_time: action.payload.startTime,
      timezone: action.payload.timeZone,
      transactions: action.payload.transactionObjectIds,
    };

    const result: AxiosResponse<{
      carrier: string;
      confirmation_code: string;
      confirmed_end_time: string;
      confirmed_start_time: string;
      timezone: string;
    }> = yield call(pickupsService.sendPickupsRequest, requestPayload);
    const payload = {
      carrier: result.data.carrier,
      confirmationCode: result.data.confirmation_code,
      pickupDate: new Date(result.data.confirmed_end_time).toJSON().slice(0, 10),
      pickupReadyByTime: result.data.confirmed_start_time,
      timeZone: result.data.timezone,
    };
    yield put(pickupsActions.sendPickupsRequestSuccess(payload));

    observability.successEvent('PICKUP_REQUEST');
  } catch (err) {
    let errorMessage =
      'There is an error from the carrier that requires investigation. Please contact support.';
    if (
      err.response &&
      err.response.data &&
      err.response.data.messages &&
      err.response.data.messages.length > 0
    )
      errorMessage = err.response.data.messages[0];

    const payload = {
      closeButtonText: 'Close',
      messages: [errorMessage],
    };
    yield put(pickupsActions.sendPickupsRequestError(errorMessage));
    yield put(showDialog(payload, 'error'));

    observability.errorEvent('PICKUP_REQUEST', { error: err?.message, errorMessage });
  }
}

export default [
  takeLatest(ActionKeys.PICKUPS_FETCH_TRANSACTIONS, getTransactions),
  takeLatest(ActionKeys.PICKUPS_FETCH_CARRIER_ACCOUNT_ID, getCarrierAccountId),
  takeLatest(
    ActionKeys.PICKUPS_FETCH_CARRIER_BUILDING_LOCATION_TYPES,
    getCarrierBuildingLocationTypes,
  ),
  takeLatest(ActionKeys.PICKUPS_SEND_PICKUPS_REQUEST, sendPickupsRequest),
];
